import { useCallback, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import useBlueskyPostModalSelector from 'containers/BlueskyPostModal/useBlueskyPostModalSelector';
import {
  blueskyUserDataSelector,
  isLoadingBlueskyUserSelector,
} from 'redux/modules/social';
import {
  AdditionalDetails,
  UseAutopostingComposerStep,
  UseAutopostingComposerStepConfig,
} from './types';
import useAdditionalDetailsStep from './useAdditionalDetailsStep';
import usePostComposerStep from './usePostComposerStep';

const useAutopostingComposerStep = (
  config: UseAutopostingComposerStepConfig,
): UseAutopostingComposerStep => {
  const {
    activeStep,
    initialValues,
    onSwitchAccount,
    onAdditionalDetailsClick,
    onGoToPostComposerStep,
    onHideModal,
    onSubmit,
  } = config;

  const isActive = activeStep === 'composer';

  const { defaultDescription } = useBlueskyPostModalSelector({
    defaultDescription: initialValues.description,
  });

  const { providerUserId } = useSelector(blueskyUserDataSelector);
  const isLoading = useSelector(isLoadingBlueskyUserSelector);

  const defaultAdditionalDetails = useMemo<AdditionalDetails>(
    () => ({
      allowQuotePost: initialValues.allowQuotePost,
      replyLevel: initialValues.replyLevel,
      customReplyAudiences: initialValues.customReplyAudiences,
    }),
    [
      initialValues.allowQuotePost,
      initialValues.customReplyAudiences,
      initialValues.replyLevel,
    ],
  );

  const [prevActiveState, setPrevActiveState] = useState<boolean>(isActive);
  const [prevInitialValues, setPrevInitialValues] = useState(initialValues);
  const [prevIsLoading, setPrevIsLoading] = useState(isLoading);
  const [description, setDescription] = useState<string>(defaultDescription);
  const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState(
    defaultAdditionalDetails,
  );
  const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetails>(
    additionalDetailsDraft,
  );

  const handleSetDefaultProps = useCallback(() => {
    setDescription(defaultDescription);
    setAdditionalDetailsDraft(defaultAdditionalDetails);
    setAdditionalDetails(defaultAdditionalDetails);
  }, [defaultAdditionalDetails, defaultDescription]);

  const handleSubmit = useCallback((): void => {
    onSubmit?.({
      blueskyDid: providerUserId,
      description,
      ...additionalDetails,
    });

    handleSetDefaultProps();
  }, [
    additionalDetails,
    description,
    handleSetDefaultProps,
    onSubmit,
    providerUserId,
  ]);

  const postComposerStep = usePostComposerStep({
    allowSubmit: Boolean(description),
    description,
    onDescriptionChange: setDescription,
    onSwitchAccount,
    onAdditionalDetailsClick,
    onHideModal,
    onSubmit: handleSubmit,
  });

  const additionalDetailsStep = useAdditionalDetailsStep({
    state: additionalDetailsDraft,
    onChange: setAdditionalDetailsDraft,
    onBackClick: () => {
      onGoToPostComposerStep();
      setAdditionalDetailsDraft(additionalDetails);
    },
    onSubmit: () => {
      onGoToPostComposerStep();
      setAdditionalDetails(additionalDetailsDraft);
    },
    onHideModal,
  });

  if (prevIsLoading && !isLoading) {
    setPrevIsLoading(isLoading);
  }

  if (prevActiveState !== isActive) {
    handleSetDefaultProps();
    setPrevActiveState(isActive);
  }

  if (!isEqual(prevInitialValues, initialValues)) {
    handleSetDefaultProps();
    setPrevInitialValues(initialValues);
  }

  return [postComposerStep, additionalDetailsStep];
};

export default useAutopostingComposerStep;
